import * as React from 'react';
import { init } from 'ramda';

import AttributeGroup from '../AttributeGroup/AttributeGroup';
import NodeQuery, {
  INodeData,
} from '../../../../QueryComponents/NodeQuery/NodeQuery';
import {
  IClientBusinessModel,
  IClientAttributeModel,
} from '../../../../../Types/ClientModelTypes';
import { getAttributeInfo } from '../../../../../Model/avenyModelUtils';
import {
  getId,
  getVersion,
  getValueFromDotPath,
} from '../../../../../Utils/dataUtils';
import ActiveDatabaseName from '../../../../../Features/Routing/ActiveDatabaseName';
import ObjectUpdateMutation from '../../../../../Features/Delsystem/_StandardRutin/Mutations/ObjectMutations/ObjectUpdateMutation';
import {
  AvenyCollectionMap,
  ModelContextConsumer,
} from '../../../../../Model/ModelContext/ModelContext';
import EditableAttribute from '../../../EditableAttribute/EditableAttribute';

interface IProps {
  collection: IClientBusinessModel;
  objectId: string | null;
  attributePaths: string[];
}

export interface IAttributeField
  extends Pick<
    React.ComponentProps<typeof EditableAttribute>,
    'debounceDelayTime'
  > {
  attribute: IClientAttributeModel;
  value: string | number | boolean | null;
  attributePath: string;
  collection: IClientBusinessModel;
  id?: string;
  _version?: number;
}

const getAttributeFieldsFromPaths = (
  collection: IClientBusinessModel,
  data: INodeData | null,
  attributePaths: string[],
  collectionMap: AvenyCollectionMap
): IAttributeField[] => {
  return attributePaths.map((path: string): IAttributeField => {
    const attributeInfo = getAttributeInfo(collection, path, collectionMap);
    // Path to the attributes parent association
    const associationPath = init(path.split('.')).join('.');

    return {
      attribute: attributeInfo.attribute,
      value: getValueFromDotPath(data, path),
      attributePath: path,

      collection: attributeInfo.collection,
      id: getId(data, associationPath),
      _version: getVersion(data, associationPath),
    };
  });
};

/**
 * En komponent som hämtar attributvärden att populera AttributeGroup komponenten med, samt hanterar mutering av värden, baserat på attributPath array gentemot collection.
 * Labels sätts automatiskt baserat på attributen.
 */
class ConnectedAttributeGroup extends React.Component<IProps> {
  render() {
    return (
      <ActiveDatabaseName>
        {(databaseName: string | null) => {
          if (databaseName === null) {
            return <div>Empty database name</div>;
          }

          return (
            <ModelContextConsumer>
              {({ avenyCollectionMap }) => (
                <NodeQuery
                  collectionName={this.props.collection.CollectionName}
                  id={this.props.objectId}
                  fieldDefs={this.props.attributePaths}
                >
                  {(loading, data) => {
                    if (loading || data) {
                      const attributeFields = getAttributeFieldsFromPaths(
                        this.props.collection,
                        data,
                        this.props.attributePaths,
                        avenyCollectionMap
                      );
                      return (
                        <ObjectUpdateMutation
                          attributeFields={attributeFields}
                          database={databaseName}
                        >
                          {mutate => {
                            return (
                              <AttributeGroup
                                collection={this.props.collection}
                                attributeFields={attributeFields}
                                loading={loading}
                                mutate={mutate}
                              />
                            );
                          }}
                        </ObjectUpdateMutation>
                      );
                    } else {
                      return <div>No Data</div>;
                    }
                  }}
                </NodeQuery>
              )}
            </ModelContextConsumer>
          );
        }}
      </ActiveDatabaseName>
    );
  }
}

export default ConnectedAttributeGroup;
